import { useEffect, useMemo, useState } from "react";
import { Download, FileText, Save } from "lucide-react";
import { Seo } from "@/components/Seo";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { Label, Textarea } from "@/components/ui/input";
import {
  getDocumentUrl,
  listDocuments,
  updateDocument,
  type DocumentKind,
  type DocumentRecord,
  type DocumentStatus,
} from "@/features/documents/repo";

const statusFilters: Array<"all" | DocumentStatus> = [
  "all",
  "new",
  "in_review",
  "approved",
  "rejected",
];

const statusBadge: Record<DocumentStatus, "default" | "warning" | "success" | "danger"> = {
  new: "warning",
  in_review: "default",
  approved: "success",
  rejected: "danger",
};

const statusLabel: Record<DocumentStatus, string> = {
  new: "New",
  in_review: "In review",
  approved: "Approved",
  rejected: "Rejected",
};

const kindLabel: Record<DocumentKind, string> = {
  bol: "BOL",
  pod: "POD",
  cdl: "CDL",
  medical_card: "Medical card",
  insurance: "Insurance",
  w9: "W-9",
  resume: "Resume",
  other: "Other",
};

export default function InternalDocuments() {
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | DocumentStatus>("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const next = await listDocuments();
      setDocuments(next);
      setSelectedId((current) => current ?? next[0]?.id ?? null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load documents.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const counts = useMemo(
    () =>
      documents.reduce(
        (acc, document) => {
          acc[document.status] += 1;
          return acc;
        },
        { new: 0, in_review: 0, approved: 0, rejected: 0 } satisfies Record<DocumentStatus, number>,
      ),
    [documents],
  );

  const visibleDocuments = useMemo(
    () => documents.filter((document) => filter === "all" || document.status === filter),
    [documents, filter],
  );

  const selected = documents.find((document) => document.id === selectedId) ?? visibleDocuments[0];

  async function saveDocument(
    document: DocumentRecord,
    changes: { status?: DocumentStatus; internalNotes?: string },
  ) {
    setSaving(true);
    setError(null);
    try {
      await updateDocument(document.id, changes);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update document.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Seo title="Documents" noindex />
      <DemoBanner />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="mt-1 max-w-2xl text-sm text-brand-500">
            Review uploaded BOLs, PODs, resumes, insurance, W-9s, and driver
            compliance documents. Files stay private and open through signed URLs.
          </p>
        </div>
        <Card className="w-full max-w-xs">
          <CardContent className="flex items-center gap-3 pt-6">
            <span className="rounded-full bg-accent-500/10 p-3 text-accent-600">
              <FileText className="h-5 w-5" />
            </span>
            <div>
              <p className="text-2xl font-bold">{documents.length}</p>
              <p className="text-xs text-brand-500">Tracked documents</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 w-full max-w-xs">
        <Label htmlFor="document-status-filter">Filter documents</Label>
        <select
          id="document-status-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as "all" | DocumentStatus)}
          className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
        >
          {statusFilters.map((value) => (
            <option key={value} value={value}>
              {value === "all" ? `All documents (${documents.length})` : `${statusLabel[value]} (${counts[value]})`}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      <div className="mt-6 grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="grid gap-3">
          {loading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : visibleDocuments.length === 0 ? (
            <EmptyState
              title="No documents found"
              body="Try another review status, or check again after drivers/owner-operators upload paperwork."
            />
          ) : (
            visibleDocuments.map((document) => (
              <button
                key={document.id}
                type="button"
                onClick={() => setSelectedId(document.id)}
                className={`rounded-lg border p-4 text-left transition hover:border-accent-300 ${
                  selected?.id === document.id
                    ? "border-accent-400 bg-accent-500/5"
                    : "border-brand-100 bg-white"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="break-words font-medium text-brand-900">
                      {kindLabel[document.kind]} {document.relatedReference && `- ${document.relatedReference}`}
                    </p>
                    <p className="mt-1 break-words text-sm text-brand-500">
                      {document.ownerName || document.ownerEmail || "Unknown owner"}
                    </p>
                  </div>
                  <Badge variant={statusBadge[document.status]}>
                    {statusLabel[document.status]}
                  </Badge>
                </div>
                <p className="mt-2 text-xs text-brand-400">
                  {formatBytes(document.sizeBytes)} - {new Date(document.createdAt).toLocaleString()}
                </p>
              </button>
            ))
          )}
        </div>

        {selected && (
          <DocumentDetail
            document={selected}
            saving={saving}
            onOpen={async () => {
              try {
                const url = await getDocumentUrl(selected.storagePath);
                if (!url) {
                  window.alert("Preview mode stores document metadata only. Connect Supabase storage to open files.");
                  return;
                }
                window.open(url, "_blank", "noopener,noreferrer");
              } catch (e) {
                setError(e instanceof Error ? e.message : "Could not open document.");
              }
            }}
            onSave={(changes) => saveDocument(selected, changes)}
          />
        )}
      </div>
    </>
  );
}

function DocumentDetail({
  document,
  saving,
  onOpen,
  onSave,
}: {
  document: DocumentRecord;
  saving: boolean;
  onOpen: () => void;
  onSave: (changes: { status?: DocumentStatus; internalNotes?: string }) => void;
}) {
  const [notes, setNotes] = useState(document.internalNotes);

  useEffect(() => {
    setNotes(document.internalNotes);
  }, [document.id, document.internalNotes]);

  return (
    <Card className="h-fit min-w-0">
      <CardHeader>
        <CardTitle>Document details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-brand-400">Type</p>
          <p className="font-medium">{kindLabel[document.kind]}</p>
        </div>
        <DetailRow label="Owner" value={document.ownerName || document.ownerEmail || "Unknown"} />
        <DetailRow label="Related reference" value={document.relatedReference || "None"} />
        <DetailRow label="Storage path" value={document.storagePath} />
        <DetailRow label="File size" value={formatBytes(document.sizeBytes)} />

        <Button variant="outline" className="w-full" onClick={onOpen}>
          <Download className="mr-1 h-4 w-4" /> Open private file
        </Button>

        <div>
          <Label htmlFor="document-status">Status</Label>
          <select
            id="document-status"
            value={document.status}
            onChange={(e) => onSave({ status: e.target.value as DocumentStatus })}
            disabled={saving}
            className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-50"
          >
            {(Object.keys(statusLabel) as DocumentStatus[]).map((status) => (
              <option key={status} value={status}>
                {statusLabel[status]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="document-notes">Internal notes</Label>
          <Textarea
            id="document-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes for safety, dispatch, accounting..."
          />
          <Button
            className="mt-2 w-full"
            variant="accent"
            disabled={saving}
            onClick={() => onSave({ internalNotes: notes })}
          >
            <Save className="mr-1 h-4 w-4" /> {saving ? "Saving..." : "Save notes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-brand-400">{label}</p>
      <p className="break-words text-sm font-medium text-brand-800">{value}</p>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes) return "Unknown size";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed border-brand-200 bg-white p-6 text-center">
      <p className="font-medium text-brand-900">{title}</p>
      <p className="mt-1 text-sm text-brand-500">{body}</p>
    </div>
  );
}
